import React, { useCallback, useMemo, useState } from "react";
import { Pane, Button, ConfirmationModal, MenuSection, KeyValue, } from "@folio/stripes/components";
import { useOkapiKy } from "@folio/stripes/core";
import { useMutation, useQueryClient } from "react-query";
import { useIntl, FormattedMessage } from "react-intl";
import NavComponent from "../../components/NavComponent";
import TableComponent from "../../components/TableComponent";
import { RecekPaneSet } from "../../components/RecekPaneSet";
import { createStripesSource } from "../../utils/createStripesSource";
import { formatCountSubtitle } from "../../utils/paneCount";
import { useFrequencyConflictsData } from "../../hooks/useFrequencyConflictsData";
import { acceptSkc, keepRecek } from "../../api/periodicalLibraryService";
import { frequencyLabelId } from "../../utils/frequencyLabel";
import { useTitleSort } from "../../hooks/useTitleSort";
import { useListErrorMessage } from "../../hooks/useListErrorMessage";
import { columnSortKeys } from "../../utils/titleSort";
import { useVariantTitlesColumn } from "../../hooks/useVariantTitlesColumn";
import { VariantTitlesList } from "../../components/common/VariantTitlesList";
import { FrequencyConflictFilters } from "../../components/periodical/FrequencyConflictFilters";
export var FrequencyConflictsList = function () {
    var _a;
    var formatMessage = useIntl().formatMessage;
    var kyInstance = useOkapiKy();
    var queryClient = useQueryClient();
    var _b = useState(true), filterPaneIsVisible = _b[0], setFilterPaneIsVisible = _b[1];
    var _c = useState(null), selectedItem = _c[0], setSelectedItem = _c[1];
    var _d = useState(false), acceptConfirmOpen = _d[0], setAcceptConfirmOpen = _d[1];
    var _e = useState(""), draftName = _e[0], setDraftName = _e[1];
    var _f = useState(""), appliedName = _f[0], setAppliedName = _f[1];
    var handleRowClick = useCallback(function (item) { return setSelectedItem(item); }, []);
    var handleCloseDetail = useCallback(function () { return setSelectedItem(null); }, []);
    var variantTitlesColumn = useVariantTitlesColumn();
    var toggleFilterPane = useCallback(function () { return setFilterPaneIsVisible(function (prev) { return !prev; }); }, []);
    var afterResolve = useCallback(function () {
        queryClient.invalidateQueries(["frequencyConflicts"]);
        queryClient.invalidateQueries(["periodicalLibraries"]);
        handleCloseDetail();
    }, [queryClient, handleCloseDetail]);
    var acceptSkcMutation = useMutation(function (id) { return acceptSkc(kyInstance, id); }, {
        onSuccess: function () {
            setAcceptConfirmOpen(false);
            afterResolve();
        },
    });
    var keepRecekMutation = useMutation(function (id) { return keepRecek(kyInstance, id); }, { onSuccess: afterResolve });
    var handleConfirmAcceptSkc = useCallback(function () {
        if (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id) {
            acceptSkcMutation.mutate(selectedItem.id);
        }
    }, [acceptSkcMutation, selectedItem]);
    var handleKeepRecek = useCallback(function () {
        if (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id) {
            keepRecekMutation.mutate(selectedItem.id);
        }
    }, [keepRecekMutation, selectedItem]);
    var columns = useMemo(function () { return [
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.periodicals.table.title",
            }),
            key: "title",
            sortKey: "name",
            alwaysVisible: true,
            clickable: true,
            onClick: handleRowClick,
        },
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.titles.table.partNumber",
            }),
            key: "partNumber",
            sortKey: "partNumber",
        },
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.titles.table.partName",
            }),
            key: "partName",
            sortKey: "partName",
        },
        variantTitlesColumn,
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.periodicals.table.issn",
            }),
            key: "isbn",
            sortKey: "issn",
        },
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.frequencyConflicts.table.recekFrequency",
            }),
            key: "frequency",
            sortKey: "frequency",
            cellRenderer: function (item) {
                return item.frequency
                    ? formatMessage({ id: frequencyLabelId(item.frequency) })
                    : "";
            },
        },
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.frequencyConflicts.table.skcFrequency",
            }),
            key: "skcFrequency",
            sortKey: "skcFrequency",
            cellRenderer: function (item) {
                return item.skcFrequency
                    ? formatMessage({ id: frequencyLabelId(item.skcFrequency) })
                    : "";
            },
        },
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.periodicals.table.publisher",
            }),
            key: "publisherNameToCheck",
            sortKey: "publisher",
        },
    ]; }, [formatMessage, handleRowClick, variantTitlesColumn]);
    var sortKeys = useMemo(function () { return columnSortKeys(columns); }, [columns]);
    var _g = useTitleSort("frequencyConflicts", sortKeys), sort = _g.sort, handleSort = _g.handleSort;
    var dataHook = useFrequencyConflictsData(sort, appliedName.trim() || undefined);
    var errorMessage = useListErrorMessage(dataHook.error);
    var handleSortAndRestartPaging = useCallback(function (sortKey) {
        dataHook.fetchMoreData(0);
        handleSort(sortKey);
    }, [dataHook, handleSort]);
    var handleApplyFilters = useCallback(function () {
        dataHook.fetchMoreData(0);
        setAppliedName(draftName);
        setSelectedItem(null);
    }, [dataHook, draftName]);
    var source = useMemo(function () {
        return createStripesSource({
            ky: null,
            data: dataHook.conflictsData,
            totalRecords: dataHook.totalRecords,
            isLoading: dataHook.isLoading,
            isError: dataHook.isError,
            errorMessage: errorMessage,
            isLoaded: true,
            fetchMore: function () {
                return dataHook.fetchMoreData(dataHook.pageOffset + dataHook.pageSize);
            },
        });
    }, [dataHook, errorMessage]);
    var detailActionMenu = function () { return (React.createElement(MenuSection, { label: formatMessage({ id: "ui-br-legaldeposits.action.menu.label" }), id: "frequency-conflict-detail-actions" },
        React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () { return setAcceptConfirmOpen(true); }, fullWidth: true },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.frequencyConflicts.action.acceptSkc" })),
        React.createElement(Button, { buttonStyle: "dropdownItem", onClick: handleKeepRecek, fullWidth: true },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.frequencyConflicts.action.keepRecek" })))); };
    return (React.createElement(RecekPaneSet, null,
        filterPaneIsVisible && (React.createElement(Pane, { id: "menuPane", defaultWidth: "25%", paneTitle: formatMessage({
                id: "ui-br-legaldeposits.pane.title.navigation",
            }) },
            React.createElement(NavComponent, null),
            React.createElement(FrequencyConflictFilters, { name: draftName, hasUnappliedChanges: draftName !== appliedName, onNameChange: setDraftName, onApplyFilters: handleApplyFilters }))),
        React.createElement(Pane, { id: "contentPane", defaultWidth: "fill", paneTitle: formatMessage({
                id: "ui-br-legaldeposits.navigation.frequency-conflicts",
            }), paneSub: formatCountSubtitle(formatMessage, dataHook.totalRecords, dataHook.isCountCapped) },
            React.createElement(TableComponent, { data: dataHook.conflictsData, columns: columns, columnPrefsKey: "frequencyConflicts", pageSize: dataHook.pageSize, onPageSizeChange: dataHook.changePageSize, totalCount: dataHook.totalRecords, onNeedMoreData: function (offset) { return dataHook.fetchMoreData(offset); }, searchTerm: "", filterPaneIsVisible: filterPaneIsVisible, toggleFilterPane: toggleFilterPane, source: source, isFetching: dataHook.isFetching, onRowClick: handleRowClick, sortBy: sort.sortBy, sortDir: sort.sortDir, onSort: handleSortAndRestartPaging, resetPaginationKey: "".concat(appliedName, "|").concat(sort.sortBy, "|").concat(sort.sortDir) })),
        selectedItem && (React.createElement(Pane, { id: "detailPane", defaultWidth: "35%", paneTitle: (_a = selectedItem.title) !== null && _a !== void 0 ? _a : "", onClose: handleCloseDetail, dismissible: true, actionMenu: detailActionMenu },
            React.createElement(VariantTitlesList, { variants: selectedItem.variantTitles }),
            React.createElement(KeyValue, { label: formatMessage({
                    id: "ui-br-legaldeposits.frequencyConflicts.table.recekFrequency",
                }), value: selectedItem.frequency
                    ? formatMessage({
                        id: frequencyLabelId(selectedItem.frequency),
                    })
                    : "" }),
            React.createElement(KeyValue, { label: formatMessage({
                    id: "ui-br-legaldeposits.frequencyConflicts.table.skcFrequency",
                }), value: selectedItem.skcFrequency
                    ? formatMessage({
                        id: frequencyLabelId(selectedItem.skcFrequency),
                    })
                    : "" }))),
        React.createElement(ConfirmationModal, { open: acceptConfirmOpen, heading: formatMessage({
                id: "ui-br-legaldeposits.frequencyConflicts.action.acceptSkc",
            }), message: formatMessage({
                id: "ui-br-legaldeposits.frequencyConflicts.acceptSkc.confirm",
            }), confirmLabel: formatMessage({
                id: "ui-br-legaldeposits.frequencyConflicts.acceptSkc.confirmButton",
            }), onConfirm: handleConfirmAcceptSkc, onCancel: function () { return setAcceptConfirmOpen(false); }, isConfirmButtonDisabled: acceptSkcMutation.isLoading })));
};
